import { useEffect, useState, createContext, useContext, ReactNode, useRef } from 'react'
import { Outlet, useLocation, useNavigation } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import 'react-datepicker/dist/react-datepicker.css'
import './styles/css/app.less'
import './styles/css/sandman.less'
import '@mdsol/onex-design/themeProvider-v2.css'
import '@mdsol/onex-design/theme-base.css'
import '@mdsol/onex-design/theme-platform.css'
import i18n from './services/i18n/i18n'
import { cssVariables } from './theme/colors'
import { SideNav } from './components/side-nav'
import LoadingSpinner from './components/LoadingSpinner'
import GlobalFooter from './components/GlobalFooter'
import { SvgSpriteSymbols } from './components/SvgSpriteSymbols'
import useTranslationLoader from './hooks/useTranslationLoader'
import useFeaturesLoader from './hooks/useFeaturesLoader'
import { ModalProvider } from './contexts/ModalContext'
import { ModalContainer } from './components/ModalContainer/index'
import { useNavigateWithParams } from './hooks/useNavigateWithParams'
import { UploaderWidget } from './services/uploader/UploaderWidget'
import Header from './components/Header'
import { textsFactory } from './services/factory/textsFactory'

// Import all API services
import * as apiServices from './services/api'
import * as aclApi from './services/api/acl'
import { aclsApiService } from './services/api/api.acls'
import * as authApi from './services/api/auth'
import * as filesApi from './services/api/files'
import * as tasksApi from './services/api/tasks'
import * as workflowsApi from './services/api/workflows'
import * as reportsApi from './services/api/reports'
import { usersApiService as usersApi } from './services/api/users'
import * as settingsApi from './services/api/api.settings'
import * as metadataApi from './services/api/api.metadata'
import * as etmfApi from './services/api/etmf'
import * as eosApi from './services/api/api.eos'
import { formFiltersService, changeRequestTypesService } from './services/api'
import { persistService } from './services/persist'
import { type UsersApiService } from './services/api/users'
import Constants from './constants/constants'
import * as tagsApi from './services/api/tags'
import * as activityApi from './services/api/api.activity'
import * as appsApi from './services/api/api.apps'
import * as autofileApi from './services/api/api.autofile'
import * as changerequesttypesApi from './services/api/api.changerequesttypes'
import * as documenttypesApi from './services/api/api.documenttypes'
import * as filesMigrationApi from './services/api/filesMigration'
import * as watermarksApi from './services/api/api.watermarks'
import * as packagesApi from './services/api/packages'
import { folderStructureTemplateTypesApiService } from './services/api/api.folderstructuretemplatetypes'
import { folderTypesApiService } from './services/api/api.foldertypes'
import { documentTypesApiService } from './services/api/api.documenttypes'
import { groupsApiService } from './services/api/api.groups'
import * as authService from './services/auth'
import * as aclService from './services/acl.service'
import * as accessService from './services/access.service'
import * as taskService from './services/task.service'
import * as pollingService from './services/polling.service'
import * as systemInformationService from './services/systemInformationService'
import * as uploadService from './services/upload'
import * as esignatureService from './services/esignature'
import * as environmentService from './services/environment'
import * as checkmateService from './services/checkmate'
import { toast } from './services/toast'
import { initService } from './services/init.service'
import { stagingService } from './services/staging.service'
import * as etmfService from './services/etmf.service'
import { formatUserDateTime } from './utils/dateFormatting'
import { getFileExtension } from './utils/fileExtension'
import { EventSystem } from './utils/eventSystem'
import * as filesUploadApi from './services/api/files-upload'
import * as eosServiceModule from './services/eos/eos.service'
import * as eosDataServiceModule from './services/eos/eosSetUpStep2.data.service'
import * as eosActionServiceModule from './services/eos/eosSetUpStep2.action.service'
import * as eosStudyServiceModule from './services/eos/eosSetUpStep2.study.service'
import * as eosCountryServiceModule from './services/eos/eosSetUpStep2.country.service'

// Define the service context interface (matching lego pattern)
export interface ServiceContextType {
  service: {
    init: typeof initService
    auth: typeof authService
    access: typeof accessService.default
    acl: typeof aclService
    task: typeof taskService
    polling: typeof pollingService
    toast: typeof toast
    systemInformation: typeof systemInformationService
    persist: typeof persistService
    upload: typeof uploadService
    esignature: typeof esignatureService
    environment: typeof environmentService
    checkmate: typeof checkmateService
    staging: typeof stagingService
    etmf: typeof etmfService
    filter: {
      translate: (key: string, params?: any) => string
      date: (value: string | number | Date | null, includeTime?: boolean) => string
      extension: (filename: string) => string
      trusted: (url: string) => string
    }
    textsFactory: typeof textsFactory
    // EOS Services (optional to avoid breaking existing test mocks)
    eosService?: typeof eosServiceModule
    EosSetUpStep2DataService?: typeof eosDataServiceModule
    EosSetUpStep2ActionService?: typeof eosActionServiceModule
    EosSetUpStep2StudyService?: typeof eosStudyServiceModule
    EosSetUpStep2CountryService?: typeof eosCountryServiceModule
    PageSize?: { get: () => number; set: (size: number) => void }
  }
  constant: typeof Constants
  api: {
    acl: typeof aclApi
    acls: typeof aclsApiService
    auth: typeof authApi
    files: typeof filesApi
    tasks: typeof tasksApi
    workflows: typeof workflowsApi
    reports: typeof reportsApi
    users: UsersApiService
    settings: typeof settingsApi
    metadata: typeof metadataApi
    etmf: typeof etmfApi
    eos: typeof eosApi.eosApiService
    formFilters: typeof formFiltersService
    changeRequestTypes: typeof changeRequestTypesService
    tags: typeof tagsApi
    documentTypes: typeof documentTypesApiService
    folderTypes: typeof folderTypesApiService
    activity: typeof activityApi
    apps: typeof appsApi
    autofile: typeof autofileApi
    changerequesttypes: typeof changerequesttypesApi
    filesMigration: typeof filesMigrationApi
    watermarks: typeof watermarksApi
    packages: typeof packagesApi
    groups: typeof groupsApiService
    folderStructureTemplateTypes: typeof folderStructureTemplateTypesApiService
    // Legacy flat access for backwards compatibility
    legacy: typeof apiServices
    filesUploadApi: typeof filesUploadApi
  }
  $rootScope: EventSystem // AngularJS-style event system
  config: {
    apiUrl: string
    checkmateApiUrl: string
    uiUrl: string
    cdnUrl: string
    loginImedidataUrl: string
    zipkinServerUrl: string
    buildTime: string
    buildId: string
    websocketsEnabled: boolean
    stageName: string
  }
  bus: any // Event bus instance
  store: {
    filterCollapsed?: boolean
    formRef?: {
      setFieldValue?: any
      validateForm?: () => Promise<Record<string, any>>
      values?: any
      setTouched?: (touched: any) => void
      options?: any
      dirty?: boolean
    }
    svg: Record<string, any>
    formRefStaging: Record<string, any>
  }
  root: React.RefObject<HTMLElement | null>
}

// Create the service context (matching lego pattern)
const ServiceContext = createContext<ServiceContextType | null>(null)

// Export context for direct useContext usage if needed
export { ServiceContext }

// Custom hook to use service context (matching lego pattern)
export const useServiceContext = (): ServiceContextType => {
  const context = useContext(ServiceContext)
  if (!context) {
    throw new Error('useServiceContext must be used within a ServiceProvider')
  }
  return context
}

// Event bus wrapper to match AngularJS pattern
class EventBus extends EventTarget {
  private eventHandlers: Map<string, Set<(data: any) => void>> = new Map()

  subscribe(eventType: string, handler: (event: any) => void): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }
    this.eventHandlers.get(eventType)!.add(handler)

    return () => {
      const handlers = this.eventHandlers.get(eventType)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          this.eventHandlers.delete(eventType)
        }
      }
    }
  }

  publish(eventObject: { type: string; payload: any }): void {
    const handlers = this.eventHandlers.get(eventObject.type)
    if (handlers) {
      handlers.forEach((handler) => handler(eventObject))
    }
  }
}

// Service provider component (matching lego pattern)
export function ServiceProvider({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLElement>(null)
  const eventBusRef = useRef(new EventTarget()) // Simple event bus replacement
  const rootScopeRef = useRef<EventSystem>(new EventSystem()) // AngularJS-style event system
  const storeRef = useRef({ svg: {}, formRefStaging: {} })

  // Get HTML element attributes for config
  const htmlEl = document.documentElement
  const getAttr = (name: string): string => htmlEl.getAttribute(name) || ''

  // Create config object from HTML attributes (matching legacy pattern)
  const config = {
    apiUrl: getAttr('api-url'),
    checkmateApiUrl: getAttr('checkmate-api-url'),
    uiUrl: getAttr('ui-url'),
    cdnUrl: getAttr('cdn-url'),
    loginImedidataUrl: getAttr('login-imedidata-url'),
    zipkinServerUrl: getAttr('zipkin-server-url'),
    buildTime: getAttr('build-time'),
    buildId: getAttr('build-id'),
    websocketsEnabled: !!(window as any).rcmApplicationConfig?.websocketsEnabled,
    stageName: getAttr('stage-name')
  }

  // Create the service structure (matching lego pattern)
  const service: ServiceContextType = {
    service: {
      init: initService,
      auth: authService,
      access: accessService.default, // Use the default export which is the singleton instance
      acl: aclService,
      task: taskService,
      polling: pollingService,
      toast: toast,
      systemInformation: systemInformationService,
      persist: persistService,
      upload: uploadService,
      esignature: esignatureService,
      environment: environmentService,
      checkmate: checkmateService,
      staging: stagingService,
      etmf: etmfService,
      filter: {
        translate: (key: string, params?: any) => {
          // Use the actual i18n translation service
          // Disable interpolation escaping to preserve special characters like &
          const result = i18n.t(key, { ...params, interpolation: { escapeValue: false } })
          return typeof result === 'string' ? result : String(result)
        },
        date: (value: string | number | Date | null, includeTime?: boolean) => {
          // Use our date formatting utility
          if (!value) return '-'
          return formatUserDateTime(value, includeTime)
        },
        extension: (filename: string) => {
          // Get file extension for icon display
          return getFileExtension(filename)
        },
        trusted: (url: string) => {
          // Return URL as-is (sanitization handled by DOMPurify where needed)
          // Simple URL sanitization - just return the URL for now
          // DOMPurify is already installed if more complex sanitization is needed
          return url
        }
      },
      textsFactory: textsFactory,
      // EOS Services
      eosService: eosServiceModule,
      EosSetUpStep2DataService: eosDataServiceModule,
      EosSetUpStep2ActionService: eosActionServiceModule,
      EosSetUpStep2StudyService: eosStudyServiceModule,
      EosSetUpStep2CountryService: eosCountryServiceModule,
      PageSize: {
        get: () => {
          const stored = localStorage.getItem('rcm_page_size')
          return stored ? parseInt(stored, 10) : 25
        },
        set: (size: number) => localStorage.setItem('rcm_page_size', String(size))
      }
    },
    constant: Constants,
    api: {
      acl: aclApi,
      acls: aclsApiService,
      auth: authApi,
      files: filesApi,
      tasks: tasksApi,
      workflows: workflowsApi,
      reports: reportsApi,
      users: usersApi,
      settings: settingsApi,
      metadata: metadataApi,
      etmf: etmfApi,
      eos: eosApi.eosApiService,
      formFilters: formFiltersService,
      changeRequestTypes: changeRequestTypesService,
      tags: tagsApi,
      documentTypes: documentTypesApiService,
      folderTypes: folderTypesApiService,
      activity: activityApi,
      apps: appsApi,
      autofile: autofileApi,
      changerequesttypes: changerequesttypesApi,
      filesMigration: filesMigrationApi,
      watermarks: watermarksApi,
      packages: packagesApi,
      groups: groupsApiService,
      folderStructureTemplateTypes: folderStructureTemplateTypesApiService,
      // Legacy flat access for backwards compatibility
      legacy: apiServices,
      filesUploadApi: filesUploadApi
    },
    $rootScope: rootScopeRef.current,
    store: {
      svg: storeRef.current.svg,
      formRefStaging: storeRef.current.formRefStaging
    },
    root: rootRef,
    config: config, // Application configuration
    bus: eventBusRef.current
  }

  return <ServiceContext.Provider value={service}>{children}</ServiceContext.Provider>
}

export function AppContent() {
  const navigate = useNavigateWithParams()
  const location = useLocation()
  const serviceContext = useServiceContext()
  const [authChecked, setAuthChecked] = useState(false)
  const [userLoggedIn, setUserLoggedIn] = useState(false)
  const [isNavCollapsed, setIsNavCollapsed] = useState(false)
  const [featuresLoaded, setFeaturesLoaded] = useState(false)
  const translationsLoaded = useTranslationLoader()
  const { data: features } = useFeaturesLoader(userLoggedIn)
  const navigation = useNavigation()

  const isLoading = navigation.state === 'loading'
  // Set tenantUuid in localStorage if present in URL/search params (legacy parity)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tenantUuid = params.get('tenantUuid')
    if (tenantUuid) {
      try {
        localStorage.setItem('tenantUuid', tenantUuid)
      } catch (e) {
        // Fallback: ignore if localStorage is unavailable
      }
    }
  }, [location.search])

  useEffect(() => {
    const storedCurrentPage = localStorage.getItem('rcm.CURRENT_PAGE')

    if (storedCurrentPage !== location.pathname + location.search) {
      localStorage.setItem('rcm.CURRENT_PAGE', location.pathname + location.search)
      localStorage.setItem('rcm.PREVIOUS_PAGE', storedCurrentPage || '')
    }
  }, [location.pathname, location.search])

  useEffect(() => {
    if (authChecked) return

    const checkAuth = async () => {
      if (!serviceContext.service.auth.isLoggedIn()) {
        try {
          await serviceContext.service.auth.fetchCurrentUser()
          setUserLoggedIn(true)
        } catch (error) {
          navigate('/login')
        }
      } else {
        setUserLoggedIn(true)
      }
      setAuthChecked(true)
    }

    checkAuth()
  }, [navigate, authChecked, serviceContext])

  // Initialize application services after authentication
  useEffect(() => {
    if (!userLoggedIn || !authChecked) return

    let cancelled = false
    const initializeApp = async () => {
      try {
        // Initialize Pendo analytics
        serviceContext.service.init.initPendo()

        // Initialize CAS activity tracker
        serviceContext.service.init.initCasActivityTracker()

        // Load features info
        await serviceContext.service.init.loadFeaturesInfo()

        // Load allowed auth types
        await serviceContext.service.init.loadAllowedAuthTypes()

        // Handle Checkmate redirects if applicable
        await serviceContext.service.init.redirectUserToOnlySiteStudyWhenCheckmated()

        // Listen to checkmate header changes (MCC navigation updates)
        try {
          serviceContext.service.checkmate.listenToCheckmateHeaderChanges()
        } catch (err) {
          console.error('Error setting up checkmate header listener:', err)
        }

        if (!cancelled) setFeaturesLoaded(true)
      } catch (error) {
        setFeaturesLoaded(true)
        console.error('Error initializing application:', error)
      }
    }

    initializeApp()
    return () => {
      cancelled = true
    }
  }, [userLoggedIn, authChecked, serviceContext])

  // Track route changes and emit checkmate actions (React equivalent of $stateChangeSuccess)
  useEffect(() => {
    if (!userLoggedIn || !featuresLoaded) return

    // Extract route name from pathname
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const routeName = pathSegments[0] || 'home'

    // Call checkmate onStateChange when route changes
    serviceContext.service.checkmate
      .onStateChange({ name: routeName })
      .catch((error) => console.error('Error in checkmate onStateChange:', error))
  }, [location.pathname, userLoggedIn, featuresLoaded, serviceContext])

  if (!translationsLoaded || !featuresLoaded) {
    return (
      <div style={{ height: '100vh', width: '100vw' }}>
        <LoadingSpinner centered size={40} />
      </div>
    )
  }

  return (
    <div>
      {isLoading && (
        <div className="global-loader">
          <LoadingSpinner size={40} centered />
        </div>
      )}
      <SvgSpriteSymbols />
      <Header />
      <div id="main" className={isNavCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}>
        <div className="mcc-row">
          <div
            style={{ zIndex: 1 }}
            id="mcc-sidebar"
            className={`mcc-col mcc-sidebar mcc-sidebar-left ${isNavCollapsed ? 'sidebar-collapsed' : ''}`}
          >
            <SideNav setIsCollapsed={setIsNavCollapsed} />
          </div>

          <div className="mcc-content mcc-col">
            <div className="d-flex align-items-stretch justify-content-start flex-column">
              <Outlet />
              <GlobalFooter />
            </div>
          </div>
        </div>
      </div>
      <ModalContainer />
      <UploaderWidget />
    </div>
  )
}

import { PageState } from './contexts/page-state'

export default function App() {
  const queryClient = new QueryClient()

  // Inject CDN URL into CSS at runtime
  useEffect(() => {
    const htmlElement = document.documentElement
    const cdnUrl = htmlElement.getAttribute('cdn-url') || ''

    if (cdnUrl && cdnUrl !== 'cdn_domain_placeholder') {
      // Fetch CSS files directly since browser CSSOM may have resolved paths already
      const linkElements = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      const cssUrls = linkElements
        .map((link) => (link as HTMLLinkElement).href)
        .filter((href) => href && (href.includes('/assets/') || href.startsWith(window.location.origin)))

      Promise.all(
        cssUrls.map((url) =>
          fetch(url)
            .then((res) => res.text())
            .catch((err) => {
              console.warn(`[CDN Injection] Failed to fetch ${url}:`, err)
              return ''
            })
        )
      ).then((cssContents) => {
        let totalReplacements = 0
        const updatedCss = cssContents
          .map((css) => {
            if (css.includes('/cdn_domain_placeholder/')) {
              const matches = css.match(/url\((['"]?)\/cdn_domain_placeholder\//g)
              if (matches) {
                totalReplacements += matches.length
              }
              // Replace all occurrences: url(/cdn_domain_placeholder/ -> url(https://cdn.com/
              return css.replace(/url\((['"]?)\/cdn_domain_placeholder\//g, `url($1${cdnUrl}/`)
            }
            return ''
          })
          .filter((css) => css.length > 0)
          .join('\n')

        if (updatedCss.length > 0) {
          const styleEl = document.createElement('style')
          styleEl.id = 'rcm-cdn-url-injector'
          styleEl.textContent = updatedCss
          document.head.appendChild(styleEl)
        }
      })
    } else if (cdnUrl === 'cdn_domain_placeholder') {
      console.log('[CDN Injection] Skipping - cdn-url is still placeholder value')
    } else if (!cdnUrl) {
      console.warn('[CDN Injection] No cdn-url attribute found on HTML element')
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <style>{cssVariables}</style>
        <ServiceProvider>
          <ModalProvider>
            <PageState>
              <AppContent />
            </PageState>
          </ModalProvider>
        </ServiceProvider>
      </I18nextProvider>
    </QueryClientProvider>
  )
}
